import * as React from "react";
import { inject, observer } from "mobx-react";
import { IngestionStore } from "Stores/index";
import { CourseMappingForm } from "./CourseMappingForm";
import {Button, Form, Table} from "react-bootstrap";
import {
    CellContext,
    Column,
    ColumnDef,
    ColumnFiltersState,
    flexRender,
    getCoreRowModel,
    getExpandedRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    PaginationState,
    Row,
    SortingState,
    useReactTable
} from "@tanstack/react-table";
import { Course, IDataQuery } from "Stores/commonStore";
import {ChangeEvent, Fragment, useEffect, useMemo, useState} from "react";
import DebouncedInput from "Components/Common/DebouncedInput";
import {Defaults} from "../../../common/utils";
import PageTitle from "Components/Common/PageTitle";
import {useInternalContext} from "@/contexts/internal";


interface CoursesListProps {
    ingestionStore?: IngestionStore
}

interface CoursesListState {
    expanded?: {[index: number]: boolean}
    showFilters: boolean
}

const urlArray: string[] = [];

export const CoursesList = inject("ingestionStore")(observer(function CoursesList(props: CoursesListProps) {
    const [state, setState] = React.useState<CoursesListState>({
        expanded: {},
        showFilters: true
    });
    const {setRenderActionButton} = useInternalContext();


    const renderFilterButton = () => (
        <div className="actions">
            <Button
                id="courses-filter-button"
                disabled={false}
                className={"btn-icon"}
                onClick={() => {
                    setState((prevState) => ({...prevState, showFilters: !prevState.showFilters}));
                }}>
                <i className="icon icon-filter"/>
            </Button>
        </div>
    );

    React.useEffect(() => {
        // Set the renderActionButton when this component is mounted
        setRenderActionButton(renderFilterButton());

        // Clear the renderActionButton when this component is unmounted
        return () => {
            setRenderActionButton(null); // Clean up
        };
    }, []);

    const renderList = () => {
        if (!props.ingestionStore) return null;
        return (
            <div className="list">
                <CoursesReactTable ingestionStore={props.ingestionStore} showFilters={state.showFilters}/>
            </div>
        );
    };

    if (!props.ingestionStore) return null;

    return (
        <>
            <PageTitle pageTitle={"Courses Management"}/>
            {renderList()}
        </>
    );
}));

interface CoursesReactTableProps {
    ingestionStore: IngestionStore
    showFilters: boolean
}

function CoursesReactTable({ingestionStore, showFilters}: CoursesReactTableProps) {
    const [dataQuery, setDataQuery] = useState<IDataQuery | null>(null);
    const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
    const [sorting, setSorting] = useState<SortingState>([]);
    const [{pageIndex, pageSize}, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: Defaults.tablePageSize()});
    const pagination = useMemo(() => ({pageIndex, pageSize}), [pageIndex, pageSize]);
    const [refreshKey, setRefreshKey] = useState(0);
    const [page, setPage] = useState(1);
    const [pageSizing] = useState(100);
    const [order, setOrder] = useState("");

    const createUrl = (columnName: string) => {
      const index = urlArray.findIndex((urlString) =>
        urlString.includes(columnName)
      );
      if (index !== -1) {
        const value = urlArray[index];
        value.includes("asc") ?  urlArray.splice(index, 1) :  urlArray[index] = `${columnName}:asc`;
      } else {
        urlArray[0] = `${columnName}:desc`;
      }
      const orderUrl = urlArray.join(",");
      setOrder(orderUrl);
    };
    const selectChange = (columnName) => {
      createUrl(columnName);
    };

    const applySortStyle = (columnName: string) => {
       if (urlArray.includes(`${columnName}:asc`)) {
        return "asc"
       } else if (urlArray.includes(`${columnName}:desc`)) {
        return "desc"
       } else {
        return ""
       }
    }


    const columns = useMemo<ColumnDef<Course>[]>(
        () => [

            {
                id: "pending",
                header: "Pending",
                accessorFn: (original) => overridePending(original),
                cell: props => {
                    if (props.getValue() === true) {
                        const icon = "table-icon-cell icon icon-adjust";
                        return (<i className={icon} />);
                    } else {
                        return null;
                    }
                },
                size: 115,
            },
            {
                header: "Needs Review",
                accessorKey: "needs_review",
                cell: props => {
                    if (props.getValue() === true) {
                        const icon = "table-icon-cell icon icon-exclamation-circle";
                        return (<i className={icon} />);
                    }
                    return null;
                },
                size: 115,
            },
            {
                header: "Disabled",
                accessorKey: "disabled",
                cell: props => {
                    if ( props.getValue() === true ) {
                        const icon = "table-icon-cell icon icon-ban";
                        return (<i className={icon} />);
                    } else {
                        return null;
                    }
                },
                size: 115,
            }, {
                header: "Name",
                accessorKey: "name",
                size: 200,
            }, {
                header: "Course ID",
                accessorKey: "course_id",
            }, {
                header: "Term",
                accessorKey: "term_name",
            }, {
                id: "expander",
                header: () => null,
                enableColumnFilter: false,
                size: 50,
                cell: (props: CellContext<Course, unknown>) => {
                    return (
                        <div>
                            <button
                                className="table-action-button"
                                onClick={ props.row.getToggleExpandedHandler() }>
                                <i className={`icon icon-${props.row.getIsExpanded() ? "compress" : "expand"}`} />
                            </button>
                        </div>
                    );
                },
            }
        ], []);
    const table = useReactTable({
        columns,
        data: dataQuery?.data ?? [],
        state: {
            columnFilters,
            sorting,
            pagination
        },
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getRowCanExpand: () => true,
        getExpandedRowModel: getExpandedRowModel(),
        onColumnFiltersChange: setColumnFilters,
        onSortingChange: setSorting,
        onPaginationChange: setPagination,
        manualFiltering: true,
    });

    async function fetchCourses() {
        let filters;
        if (columnFilters.length > 0) {
            filters = {};
            columnFilters.forEach(filter => {
                if (filter.id === "disabled" || filter.id === "pending") {
                    if (filter.value === "any") {
                        return;
                    } else {
                        filters[filter.id] = {operator: "eq", value: filter.value === "yes"};
                    }
                } else {
                    filters[filter.id] = {operator: "contains", value: filter.value};
                }
            });
        }
        await ingestionStore.fetchPaginatedEnfluxCourses(pageSizing, page, order, filters);
        setDataQuery(ingestionStore.enfluxCourses as unknown as IDataQuery);
    }

    useEffect(() => {
        fetchCourses().catch(() => {});
    }, [page, pageSizing, order, columnFilters, refreshKey]);

    const findOverride = (mappings: []) => {
        if (!mappings) return null;
        return mappings.find(({ source_table }) => source_table === "enflux_user_course_overrides");
    };

    const overridePending = (originalRow: any) => {
        if (!originalRow) return false;

        const override: any = findOverride(originalRow.mappings);
        if (!override) return false;

        const courseUpdatedDate = new Date(originalRow.updated);
        const overrideUpdatedDate = new Date(override.updated);

        return overrideUpdatedDate > courseUpdatedDate;
    };

    const renderSubComponent = (row: Row<Course>) => {
        return (
            <CourseMappingForm
                handleSave={() => {
                    row.toggleExpanded();
                    setRefreshKey(prevState => prevState + 1);
                }}
                handleCancel={() => row.toggleExpanded()}
                mapping={ row.original } />
        );
    };
    return (
        <>
            <Table>
                <thead>
                {table.getHeaderGroups().map(headerGroup => (
                    <tr key={headerGroup.id}>
                        {headerGroup.headers.map(header => (
                            <th key={header.id}>
                                {header.isPlaceholder
                                    ? null
                                    : (
                                        <>
                                            <div

                                                className={header.column.getCanSort() ? "cursor-pointer " + `${applySortStyle(header.column.id)}` : ""}
                                                onClick={() => selectChange(header.column.id)}
                                                >
                                                {flexRender(
                                                    header.column.columnDef.header,
                                                    header.getContext()
                                                )}
                                            </div>
                                            {!showFilters ? null : (header.column.getCanFilter() && (
                                                <div>
                                                    <Filter column={header.column}/>
                                                </div>) || <div className="fill-no-filter"></div>)}
                                        </>
                                    )}
                            </th>
                        ))}
                    </tr>
                ))}
                </thead>
                <tbody>
                {table.getRowModel().rows.map(row => (
                    <Fragment key={row.id}>
                        <tr>
                            {row.getVisibleCells().map(cell => (
                                <td key={cell.id}>
                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                </td>
                            ))}
                        </tr>
                        {row.getIsExpanded() && (
                            <tr>
                                <td colSpan={row.getVisibleCells().length}>
                                    {renderSubComponent(row)}
                                </td>
                            </tr>
                        )}
                    </Fragment>
                ))}
                </tbody>
            </Table>
            <div className={"pagination-bottom"}>
                <div className={"pagination"}>
                    <div className={"previous d-grid gap-2"}>
                        <Button disabled={page === 1} onClick={() => {
                            if (page === 1) return;
                            setPage(page - 1)}}
                            >Previous
                        </Button>
                    </div>
                    <div className={"center"}>
                        <span className={"page-info"}>
                            Page
                            <div className={"page-jump"}>
                                <Form.Control
                                    type="number"
                                    value={dataQuery?.current_page}
                                    onChange={e => {
                                        const page = e.target.value ? Number(e.target.value) : 1;
                                        if (dataQuery) {
                                            if (page >= 1 && page <= dataQuery.total_pages) {
                                                setPage(page);
                                        }}
                                    }}/>
                            </div>
                            of {" "}
                            <span className={"total-pages"}>{dataQuery?.total_pages}</span>
                        </span>
                    </div>
                    <div className={"next d-grid gap-2"}>
                        <Button disabled={page === dataQuery?.total_pages} onClick={() => {
                            if (page === dataQuery?.total_pages) return;
                            setPage(page + 1)}}
                        >Next</Button>
                    </div>
                </div>
            </div>
        </>
    );
}

interface FilterProps {
    column: Column<any, unknown>
}

function Filter({column}: FilterProps) {
    const columnFilterValue = column.getFilterValue();
    const onChangeSelect = (e: ChangeEvent<HTMLSelectElement>) => {
        column.setFilterValue(e.target.value);
    };

    if (column.id === "pending" || column.id === "needs_review" || column.id === "disabled") {
        return (
            <Form.Select onChange={onChangeSelect}>
                <option value={"any"}>Any</option>
                <option value={"yes"}>Yes</option>
                <option value={"no"}>No</option>
            </Form.Select>
        );
    }

    return (
        <DebouncedInput type={"text"} value={(columnFilterValue ?? "") as string} onChange={(value) => column.setFilterValue(value)} />
    );
}